/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type * as Types from '../../../__generated__/zeus/types';

import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';
/** Result of canceling a buyer purchase offer. */
export type BuyerPurchaseOfferCancelStatus =
  /** Offer was canceled. */
  | 'OFFER_CANCELLED'
  /** A task was created to complete cancellation. */
  | 'TASK_CREATED'
  /** Cancellation result is not known. */
  | 'UNKNOWN';

/** Home type used to classify a marketplace listing. */
export type MarketplaceDwellingType =
  /** A condominium home. */
  | 'CONDO'
  /** A detached or single-family house. */
  | 'HOUSE'
  /** A property containing multiple residential units. */
  | 'MULTI_FAMILY'
  /** A dwelling type that does not fit the listed categories. */
  | 'OTHER'
  /** A townhouse. */
  | 'TOWNHOUSE';

/** Kind of action a buyer can take from a purchase task. */
export type PurchaseOfferActionType =
  /** Open an in-product modal. */
  | 'DISPLAY_MODAL'
  /** Open a link outside the purchase workflow. */
  | 'EXTERNAL_LINK'
  /** Start a purchase task. */
  | 'INITIATE_TASK'
  /** Reveal content that is hidden until requested. */
  | 'REVEAL_CONTENT';

/** Visual priority of a purchase task action. */
export type PurchaseOfferActionVariant =
  /** Primary action for the current task. */
  | 'PRIMARY'
  /** Secondary action for the current task. */
  | 'SECONDARY';

/** Severity of a customer-visible purchase offer notice. */
export type PurchaseOfferNoticeType =
  /** Notice requiring customer attention. */
  | 'CRITICAL';

/** Lifecycle state of a buyer purchase offer. */
export type PurchaseOfferState =
  /** Offer is active. */
  | 'ACTIVE'
  /** Offer is under attorney review. */
  | 'ATTORNEY_REVIEW'
  /** Offer awaits the buyer’s signature. */
  | 'AWAITING_CUSTOMER_SIGNATURE'
  /** The backup offer expired after the property sold or became unavailable. */
  | 'BACKUP_EXPIRED'
  /** Offer is a backup offer. */
  | 'BACKUP_OFFER'
  /** Buyer withdrew from the offer. */
  | 'BUYER_WALKED'
  /** Offer was canceled. */
  | 'CANCELED'
  /** Purchase was completed and closed. */
  | 'CLOSED'
  /** Offer contingencies have been released. */
  | 'CONTINGENCIES_RELEASED'
  /** The purchase contract fell through. */
  | 'CONTRACT_FELL_THROUGH'
  /** A counteroffer is pending. */
  | 'COUNTER_OFFER_PENDING'
  /** Offer is under contract. */
  | 'IN_CONTRACT'
  /** Offer is being negotiated. */
  | 'IN_NEGOTIATION'
  /** Offer was rejected. */
  | 'REJECTED'
  /** Offer status is not recognized. */
  | 'UNKNOWN';

/** Icon category for a purchase task group. */
export type PurchaseOfferTaskGroupIcon =
  /** Icon for escrow and closing tasks. */
  | 'CLIPBOARD_SEARCH'
  /** Icon for access and welcome-home tasks. */
  | 'DOOR'
  /** Icon for offer and contract tasks. */
  | 'FILE_DESCRIPTION';

/** Purchase workflow phase represented by a task group. */
export type PurchaseOfferTaskGroupPhase =
  /** Tasks for escrow and closing. */
  | 'ESCROW_CLOSING'
  /** Tasks for reviewing and completing the offer. */
  | 'OFFER'
  /** Tasks for receiving the home after closing. */
  | 'WELCOME_HOME';

/** Completion state of a purchase offer task. */
export type PurchaseOfferTaskState =
  /** Task is complete. */
  | 'COMPLETE'
  /** Task still needs attention. */
  | 'PENDING';

/** Type of task in the buyer purchase offer workflow. */
export type PurchaseOfferTaskType =
  /** Receive keys after closing. */
  | 'ACCESS_KEYS'
  /** Find and book a home inspection. */
  | 'BOOK_INSPECTION'
  /** Confirm the financing for the offer. */
  | 'CONFIRM_FINANCING'
  /** Complete the remaining closing steps. */
  | 'FINALIZE_CLOSING'
  /** Transfer the earnest money deposit. */
  | 'MAKE_DEPOSIT'
  /** Complete the mortgage application. */
  | 'MORTGAGE_APPLICATION'
  /** Complete outstanding mortgage tasks. */
  | 'MORTGAGE_TASKS'
  /** Get the property access code. */
  | 'RECEIVE_ACCESS_CODE'
  /** Review the purchase offer details. */
  | 'REVIEW_OFFER'
  /** Review and sign the purchase contract. */
  | 'SIGN_CONTRACT';

export type BuyerPurchaseOfferQueryVariables = Exact<{
  offerId: Types.Scalars['UUID']['input'];
}>;


export type BuyerPurchaseOfferQuery = { customerAccount: { buyer: { offers: Array<{ id: Types.Scalars['ID']['output'], priceCents: Types.Scalars['SafeInt']['output'] | null, hasHeroHomeCredit: Types.Scalars['Boolean']['output'], requestedEarlyCheckIn: Types.Scalars['Boolean']['output'], requestedHomeWarranty: Types.Scalars['Boolean']['output'], tradeInAddress: Types.Scalars['String']['output'] | null, transactionState: Types.PurchaseOfferState, closeOfEscrowDate: Types.Scalars['Date']['output'] | null, resaleAgent: { email: Types.Scalars['EmailAddress']['output'] | null, name: Types.Scalars['String']['output'] | null, photoUrl: Types.Scalars['URL']['output'] | null, phoneNumber: Types.Scalars['PhoneNumber']['output'] | null } | null, listing: { marketplaceListing: { addressId: Types.Scalars['UUID']['output'], slug: Types.Scalars['String']['output'] | null, street: Types.Scalars['String']['output'] | null, city: Types.Scalars['String']['output'] | null, state: Types.Scalars['String']['output'] | null, postalCode: Types.Scalars['PostalCode']['output'] | null, bedrooms: Types.Scalars['SafeInt']['output'] | null, bathrooms: Types.Scalars['Float']['output'] | null, dwellingType: Types.MarketplaceDwellingType, listPriceCents: Types.Scalars['SafeInt']['output'] | null, totalLivingSquareFootage: Types.Scalars['SafeInt']['output'] | null, yearBuilt: Types.Scalars['SafeInt']['output'] | null, photoUrls: Array<Types.Scalars['URL']['output']> } | null } | null, notices: Array<{ type: Types.PurchaseOfferNoticeType, message: Types.Scalars['NonEmptyString']['output'] }>, taskGroups: Array<{ title: Types.Scalars['NonEmptyString']['output'], icon: Types.PurchaseOfferTaskGroupIcon, phase: Types.PurchaseOfferTaskGroupPhase, progress: { completed: Types.Scalars['SafeInt']['output'], total: Types.Scalars['SafeInt']['output'] } | null, tasks: Array<
            | { __typename: 'PurchaseOfferActionTask', defaultExpanded: Types.Scalars['Boolean']['output'], id: Types.Scalars['ID']['output'], taskType: Types.PurchaseOfferTaskType, title: Types.Scalars['NonEmptyString']['output'], description: Types.Scalars['String']['output'], state: Types.PurchaseOfferTaskState | null, expandedContent: { instructions: Types.Scalars['String']['output'] | null, bulletPoints: Array<Types.Scalars['String']['output']> | null } | null, action: { label: Types.Scalars['NonEmptyString']['output'], type: Types.PurchaseOfferActionType, variant: Types.PurchaseOfferActionVariant, payload:
                  | { __typename: 'PurchaseOfferExternalLinkPayload', url: Types.Scalars['URL']['output'], openInNewTab: Types.Scalars['Boolean']['output'] }
                  | { __typename: 'PurchaseOfferModalPayload', modalId: Types.Scalars['NonEmptyString']['output'] }
                 | null } }
            | { __typename: 'PurchaseOfferInformationalTask', defaultExpanded: Types.Scalars['Boolean']['output'], id: Types.Scalars['ID']['output'], taskType: Types.PurchaseOfferTaskType, title: Types.Scalars['NonEmptyString']['output'], description: Types.Scalars['String']['output'], state: Types.PurchaseOfferTaskState | null, expandedContent: { instructions: Types.Scalars['String']['output'] | null, bulletPoints: Array<Types.Scalars['String']['output']> | null } | null }
          > }> }> } } };

export type HubPurchaseOffersQueryVariables = Exact<{ [key: string]: never; }>;


export type HubPurchaseOffersQuery = { customerAccount: { buyer: { offers: Array<{ id: Types.Scalars['ID']['output'], transactionState: Types.PurchaseOfferState, priceCents: Types.Scalars['SafeInt']['output'] | null, listing: { marketplaceListing: { addressId: Types.Scalars['UUID']['output'], street: Types.Scalars['String']['output'] | null, city: Types.Scalars['String']['output'] | null, state: Types.Scalars['String']['output'] | null, postalCode: Types.Scalars['PostalCode']['output'] | null, bedrooms: Types.Scalars['SafeInt']['output'] | null, bathrooms: Types.Scalars['Float']['output'] | null, totalLivingSquareFootage: Types.Scalars['SafeInt']['output'] | null, photoUrls: Array<Types.Scalars['URL']['output']> } | null } | null, taskGroups: Array<{ title: Types.Scalars['NonEmptyString']['output'], phase: Types.PurchaseOfferTaskGroupPhase, progress: { completed: Types.Scalars['SafeInt']['output'], total: Types.Scalars['SafeInt']['output'] } | null, tasks: Array<
            | { title: Types.Scalars['NonEmptyString']['output'], state: Types.PurchaseOfferTaskState | null }
            | { title: Types.Scalars['NonEmptyString']['output'], state: Types.PurchaseOfferTaskState | null }
          > }> }> } } };

export type NavPurchaseOffersQueryVariables = Exact<{ [key: string]: never; }>;


export type NavPurchaseOffersQuery = { customerAccount: { buyer: { offers: Array<{ id: Types.Scalars['ID']['output'], transactionState: Types.PurchaseOfferState, listing: { marketplaceListing: { street: Types.Scalars['String']['output'] | null } | null } | null }> } } };

export type BuyerPurchaseOfferAccessCodeQueryVariables = Exact<{
  offerId: Types.Scalars['UUID']['input'];
}>;


export type BuyerPurchaseOfferAccessCodeQuery = { customerAccount: { buyer: { offers: Array<{ id: Types.Scalars['ID']['output'], accessCode: Types.Scalars['NonEmptyString']['output'] | null }> } } };

export type BuyerPurchaseOfferContractRequestMutationVariables = Exact<{
  offerId: Types.Scalars['UUID']['input'];
}>;


export type BuyerPurchaseOfferContractRequestMutation = { buyerPurchaseOfferContractRequest:
    | { __typename: 'CommandResult', success: Types.Scalars['Boolean']['output'] }
    | { __typename: 'InvalidInputError' }
    | { __typename: 'NotFoundError' }
    | { __typename: 'UnauthorizedError' }
   };

export type BuyerPurchaseOfferCancelMutationVariables = Exact<{
  offerId: Types.Scalars['UUID']['input'];
}>;


export type BuyerPurchaseOfferCancelMutation = { buyerPurchaseOfferCancel:
    | { __typename: 'BuyerPurchaseOfferCancelPayload', status: Types.BuyerPurchaseOfferCancelStatus }
    | { __typename: 'InvalidInputError' }
    | { __typename: 'NotFoundError' }
    | { __typename: 'UnauthorizedError' }
   };

export class TypedDocumentString<TResult, TVariables>
  extends String
  implements DocumentTypeDecoration<TResult, TVariables>
{
  __apiType?: NonNullable<DocumentTypeDecoration<TResult, TVariables>['__apiType']>;
  private value: string;
  public __meta__?: Record<string, any> | undefined;

  constructor(value: string, __meta__?: Record<string, any> | undefined) {
    super(value);
    this.value = value;
    this.__meta__ = __meta__;
  }

  override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
    return this.value;
  }
}

export const BuyerPurchaseOfferDocument = /*#__PURE__*/ new TypedDocumentString(`
    query BuyerPurchaseOffer($offerId: UUID!) {
  customerAccount {
    buyer {
      offers(offerId: $offerId) {
        id
        priceCents
        hasHeroHomeCredit
        requestedEarlyCheckIn
        requestedHomeWarranty
        tradeInAddress
        transactionState
        closeOfEscrowDate
        resaleAgent {
          email
          name
          photoUrl
          phoneNumber
        }
        listing {
          marketplaceListing {
            addressId
            slug
            street
            city
            state
            postalCode
            bedrooms
            bathrooms
            dwellingType
            listPriceCents
            totalLivingSquareFootage
            yearBuilt
            photoUrls(first: 1)
          }
        }
        notices {
          type
          message
        }
        taskGroups {
          title
          icon
          phase
          progress {
            completed
            total
          }
          tasks {
            __typename
            id
            taskType
            title
            description
            state
            ... on PurchaseOfferInformationalTask {
              defaultExpanded
              expandedContent {
                instructions
                bulletPoints
              }
            }
            ... on PurchaseOfferActionTask {
              defaultExpanded
              expandedContent {
                instructions
                bulletPoints
              }
              action {
                label
                type
                variant
                payload {
                  __typename
                  ... on PurchaseOfferExternalLinkPayload {
                    url
                    openInNewTab
                  }
                  ... on PurchaseOfferModalPayload {
                    modalId
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<BuyerPurchaseOfferQuery, BuyerPurchaseOfferQueryVariables>;
export const HubPurchaseOffersDocument = /*#__PURE__*/ new TypedDocumentString(`
    query HubPurchaseOffers {
  customerAccount {
    buyer {
      offers {
        id
        transactionState
        priceCents
        listing {
          marketplaceListing {
            addressId
            street
            city
            state
            postalCode
            bedrooms
            bathrooms
            totalLivingSquareFootage
            photoUrls(first: 1)
          }
        }
        taskGroups {
          title
          phase
          progress {
            completed
            total
          }
          tasks {
            title
            state
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<HubPurchaseOffersQuery, HubPurchaseOffersQueryVariables>;
export const NavPurchaseOffersDocument = /*#__PURE__*/ new TypedDocumentString(`
    query NavPurchaseOffers {
  customerAccount {
    buyer {
      offers {
        id
        transactionState
        listing {
          marketplaceListing {
            street
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<NavPurchaseOffersQuery, NavPurchaseOffersQueryVariables>;
export const BuyerPurchaseOfferAccessCodeDocument = /*#__PURE__*/ new TypedDocumentString(`
    query BuyerPurchaseOfferAccessCode($offerId: UUID!) {
  customerAccount {
    buyer {
      offers(offerId: $offerId) {
        id
        accessCode
      }
    }
  }
}
    `) as unknown as TypedDocumentString<BuyerPurchaseOfferAccessCodeQuery, BuyerPurchaseOfferAccessCodeQueryVariables>;
export const BuyerPurchaseOfferContractRequestDocument = /*#__PURE__*/ new TypedDocumentString(`
    mutation BuyerPurchaseOfferContractRequest($offerId: UUID!) {
  buyerPurchaseOfferContractRequest(input: {offerId: $offerId}) {
    __typename
    ... on CommandResult {
      success
    }
  }
}
    `) as unknown as TypedDocumentString<BuyerPurchaseOfferContractRequestMutation, BuyerPurchaseOfferContractRequestMutationVariables>;
export const BuyerPurchaseOfferCancelDocument = /*#__PURE__*/ new TypedDocumentString(`
    mutation BuyerPurchaseOfferCancel($offerId: UUID!) {
  buyerPurchaseOfferCancel(input: {offerId: $offerId}) {
    __typename
    ... on BuyerPurchaseOfferCancelPayload {
      status
    }
  }
}
    `) as unknown as TypedDocumentString<BuyerPurchaseOfferCancelMutation, BuyerPurchaseOfferCancelMutationVariables>;